import React, { useState, useMemo } from 'react';
import {
  ArrowLeftIcon,
  PlusIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  PencilIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { isPrestation } from '../types/Block';
import {
  Consultation,
  ConsultationCompany,
  ConsultationCompanyLine,
  generateMockOffers,
} from '../types/Consultation';
import { MOCK_FOURNISSEURS } from '../data/fournisseurs';

interface ConsultationPageProps {
  consultation: Consultation;
  onBack: () => void;
  onUpdate: (updated: Consultation) => void;
  onRetain: (companyId: string) => void;
}

// ─── helpers ──────────────────────────────────────────────────────────────

const parseNum = (s?: string | number | null) =>
  typeof s === 'number' ? s : parseFloat(String(s ?? '0')) || 0;
const parsePU  = (s?: string) => parseNum(s);
const parseQty = (s?: string | number | null) => { const v = parseNum(s); return v > 0 ? v : 1; };

function fmt(n: number) {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function diffPct(internal: number, offer: number) {
  if (internal === 0) return 0;
  return Math.round(((offer - internal) / internal) * 100);
}

// ─── Editable inline cell ─────────────────────────────────────────────────

function EditableCell({
  value, onChange, placeholder = '—', numeric = false, colorClass = '',
}: {
  value: string; onChange: (v: string) => void;
  placeholder?: string; numeric?: boolean; colorClass?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const commit = () => { onChange(draft); setEditing(false); };

  if (editing) {
    return (
      <input autoFocus type={numeric ? 'number' : 'text'} value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        className={`w-full px-1 py-0.5 border border-violet-400 rounded text-xs outline-none ${numeric ? 'text-right' : ''}`}
        onClick={e => e.stopPropagation()}
      />
    );
  }
  return (
    <span onClick={() => { setDraft(value); setEditing(true); }}
      className={`cursor-pointer hover:bg-blue-50 rounded px-0.5 py-0.5 block text-xs ${colorClass}`}>
      {value || <span className="text-gray-300 select-none">{placeholder}</span>}
    </span>
  );
}

// ─── Status stepper ───────────────────────────────────────────────────────

const STEPS = [
  { key: 'draft'       as const, label: 'Brouillon' },
  { key: 'sent'        as const, label: 'Envoyée'   },
  { key: 'attributed'  as const, label: 'Attribuée' },
];
const STATUS_ORDER = { draft: 0, sent: 1, attributed: 2 };

// ─── Export CSV ───────────────────────────────────────────────────────────

function exportConsultationCSV(consultation: Consultation) {
  const prestations = consultation.blocks.filter(isPrestation);
  const headers = ['Désignation', 'Unité', 'Quantité', 'PU estimé (€)', 'Total estimé (€)', 'Votre P.U. (€)', 'Vos quantités', 'Notes'];
  const rows = prestations.map(b => [
    b.content.intitule || b.content.titre || '',
    b.content.unite || '', b.content.quantite || '1',
    b.content.prixUnitaire || '0', b.content.prixTotal || '0', '', '', '',
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `${consultation.name.replace(/[^a-z0-9]/gi, '_')}_consultation.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Company search modal ─────────────────────────────────────────────────

function CompanySearchModal({
  consultation, prestations, onUpdate, onClose,
}: {
  consultation: Consultation;
  prestations: ReturnType<typeof Array.prototype.filter>;
  onUpdate: (c: Consultation) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [manualName, setManualName] = useState('');
  const addedIds = new Set(consultation.companies.map(c => c.id));

  const { selected, unselected } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = MOCK_FOURNISSEURS.filter(f =>
      !q || f.name.toLowerCase().includes(q) ||
            f.lot.toLowerCase().includes(q)  ||
            f.ville.toLowerCase().includes(q)
    );
    return {
      selected:   all.filter(f =>  addedIds.has(f.id)),
      unselected: all.filter(f => !addedIds.has(f.id)),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, consultation.companies]);

  const toggle = (fId: string, fName: string) => {
    if (addedIds.has(fId)) {
      onUpdate({
        ...consultation,
        companies: consultation.companies.filter(c => c.id !== fId),
        offers:    consultation.offers.filter(o => o.companyId !== fId),
        retainedCompanyId: consultation.retainedCompanyId === fId ? undefined : consultation.retainedCompanyId,
        status: consultation.retainedCompanyId === fId && consultation.status === 'attributed' ? 'sent' : consultation.status,
      });
    } else {
      const nc: ConsultationCompany = { id: fId, name: fName };
      onUpdate({
        ...consultation,
        companies: [...consultation.companies, nc],
        offers:    [...consultation.offers, ...generateMockOffers(prestations, [nc])],
      });
    }
  };

  const addManual = () => {
    const name = manualName.trim();
    if (!name) return;
    const nc: ConsultationCompany = { id: `co-${Date.now()}`, name };
    onUpdate({
      ...consultation,
      companies: [...consultation.companies, nc],
      offers:    [...consultation.offers, ...generateMockOffers(prestations, [nc])],
    });
    setManualName('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-800">Entreprises à consulter</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Nom, lot ou ville…"
            className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400" />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
          )}
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
          {selected.map(f => (
            <label key={f.id} className="flex items-center gap-3 px-4 py-2.5 bg-violet-50 hover:bg-violet-100 cursor-pointer">
              <input type="checkbox" checked readOnly onChange={() => toggle(f.id, f.name)}
                className="w-4 h-4 accent-violet-600 flex-shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-800">{f.name}</span>
                <span className="text-xs text-gray-400 ml-2">{f.lot} — {f.ville}</span>
              </span>
            </label>
          ))}

          {selected.length > 0 && unselected.length > 0 && (
            <div className="px-4 py-1.5 bg-gray-50">
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Base de données</span>
            </div>
          )}

          {unselected.map(f => (
            <label key={f.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={false} onChange={() => toggle(f.id, f.name)}
                className="w-4 h-4 accent-violet-600 flex-shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="text-sm text-gray-700">{f.name}</span>
                <span className="text-xs text-gray-400 ml-2">{f.lot} — {f.ville}</span>
              </span>
            </label>
          ))}

          {selected.length === 0 && unselected.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">Aucun résultat pour « {search} »</p>
          )}
        </div>

        {/* Manual add */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-gray-200 bg-gray-50">
          <input value={manualName} onChange={e => setManualName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addManual()}
            placeholder="Ajouter manuellement un nom…"
            className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent" />
          <button type="button" onClick={addManual} disabled={!manualName.trim()}
            className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium disabled:opacity-40 flex-shrink-0">
            <PlusIcon className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-1.5 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 font-medium">
            Valider ({consultation.companies.length} sélectionnée{consultation.companies.length > 1 ? 's' : ''})
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

const ConsultationPage: React.FC<ConsultationPageProps> = ({ consultation, onBack, onUpdate, onRetain }) => {
  const [editingName, setEditingName]   = useState(false);
  const [nameValue, setNameValue]       = useState(consultation.name);
  const [showModal, setShowModal]       = useState(false);

  const prestations = useMemo(() => consultation.blocks.filter(isPrestation), [consultation.blocks]);
  const retainedCompany = consultation.companies.find(c => c.id === consultation.retainedCompanyId);

  // ── Derived ──────────────────────────────────────────────────────────────

  const getOffer = (companyId: string, blockId: string) =>
    consultation.offers.find(o => o.companyId === companyId && o.blockId === blockId);

  const internalTotal = prestations.reduce(
    (s, b) => s + parsePU(b.content.prixUnitaire) * parseQty(b.content.quantite), 0
  );

  /** Partial sum of all filled offers + company-specific lines */
  const companyStats = (companyId: string) => {
    let total  = 0;
    let filled = 0;
    for (const b of prestations) {
      const offer = getOffer(companyId, b.id);
      if (offer?.prixUnitaire !== null && offer?.prixUnitaire !== undefined) {
        const qty = offer.quantite !== null && offer.quantite !== undefined
          ? offer.quantite : parseQty(b.content.quantite);
        total  += offer.prixUnitaire * qty;
        filled++;
      }
    }
    // Extra lines added by this company
    for (const line of (consultation.companyLines ?? []).filter(l => l.companyId === companyId)) {
      if (line.prixUnitaire !== null && line.prixUnitaire !== undefined) {
        total += line.prixUnitaire * (line.quantite ?? 1);
      }
    }
    return { total, filled, count: prestations.length };
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleRenameSave = () => {
    const t = nameValue.trim();
    if (t) onUpdate({ ...consultation, name: t });
    setEditingName(false);
  };

  const handleRemoveCompany = (id: string) => {
    onUpdate({
      ...consultation,
      companies: consultation.companies.filter(c => c.id !== id),
      offers:    consultation.offers.filter(o => o.companyId !== id),
      retainedCompanyId: consultation.retainedCompanyId === id ? undefined : consultation.retainedCompanyId,
      status: consultation.retainedCompanyId === id && consultation.status === 'attributed' ? 'sent' : consultation.status,
    });
  };

  const handleOfferChange = (companyId: string, blockId: string, field: 'prixUnitaire' | 'quantite' | 'unite', raw: string) => {
    const offers = consultation.offers.map(o => {
      if (o.companyId !== companyId || o.blockId !== blockId) return o;
      if (field === 'unite') return { ...o, unite: raw || null };
      const n = parseFloat(raw);
      return { ...o, [field]: isNaN(n) ? null : n };
    });
    onUpdate({ ...consultation, offers });
  };

  const handleAdvanceStatus = () => {
    if      (consultation.status === 'draft') onUpdate({ ...consultation, status: 'sent' });
    else if (consultation.status === 'sent')  onUpdate({ ...consultation, status: 'draft' });
  };

  const handleRetainCompany = (companyId: string) => {
    onUpdate({ ...consultation, status: 'attributed', retainedCompanyId: companyId });
    onRetain(companyId);
  };

  const handleAddCompanyLine = (companyId: string) => {
    const newLine: ConsultationCompanyLine = {
      id: `cl-${Date.now()}`,
      companyId,
      intitule: '',
      unite: null,
      quantite: null,
      prixUnitaire: null,
    };
    onUpdate({ ...consultation, companyLines: [...(consultation.companyLines ?? []), newLine] });
  };

  const handleUpdateCompanyLine = (lineId: string, updates: Partial<ConsultationCompanyLine>) => {
    onUpdate({
      ...consultation,
      companyLines: (consultation.companyLines ?? []).map(l => l.id === lineId ? { ...l, ...updates } : l),
    });
  };

  const handleRemoveCompanyLine = (lineId: string) => {
    onUpdate({ ...consultation, companyLines: (consultation.companyLines ?? []).filter(l => l.id !== lineId) });
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const currentOrder = STATUS_ORDER[consultation.status];

  return (
    <>
      {showModal && (
        <CompanySearchModal
          consultation={consultation}
          prestations={prestations}
          onUpdate={onUpdate}
          onClose={() => setShowModal(false)}
        />
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-200 flex-wrap gap-y-2">
          <button type="button" onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors flex-shrink-0">
            <ArrowLeftIcon className="w-4 h-4" /> Retour
          </button>

          <div className="flex items-center gap-2 flex-1 min-w-0">
            {editingName ? (
              <input autoFocus value={nameValue} onChange={e => setNameValue(e.target.value)}
                onBlur={handleRenameSave}
                onKeyDown={e => { if (e.key === 'Enter') handleRenameSave(); if (e.key === 'Escape') setEditingName(false); }}
                className="text-base font-semibold text-gray-900 border-b-2 border-violet-400 outline-none bg-transparent w-56" />
            ) : (
              <button type="button" onClick={() => setEditingName(true)} className="flex items-center gap-1.5 group min-w-0">
                <h1 className="text-base font-semibold text-gray-900 truncate">{consultation.name}</h1>
                <PencilIcon className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
              </button>
            )}
          </div>

          <p className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
            {prestations.length} prestation{prestations.length > 1 ? 's' : ''}
            {' · '}Estimé :&nbsp;<strong className="text-violet-600">{fmt(internalTotal)} €</strong>
          </p>

          <button type="button" onClick={() => exportConsultationCSV(consultation)}
            title="Exporter grille CSV"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0">
            <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Exporter
          </button>

          <button type="button" disabled title="Disponible prochainement"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-300 border border-gray-200 rounded-lg cursor-not-allowed flex-shrink-0">
            <ArrowUpTrayIcon className="w-3.5 h-3.5" /> Importer
          </button>
        </div>

        {/* ── Status stepper ── */}
        <div className="flex items-center gap-1 px-5 py-2.5 bg-gray-50 border-b border-gray-200">
          {STEPS.map((step, i) => {
            const stepOrder = STATUS_ORDER[step.key];
            const isDone    = currentOrder > stepOrder;
            const isActive  = currentOrder === stepOrder;
            const isAttr    = step.key === 'attributed';

            return (
              <React.Fragment key={step.key}>
                {i > 0 && <div className={`flex-shrink-0 h-px w-6 mx-1 ${isDone ? 'bg-violet-400' : 'bg-gray-200'}`} />}
                <button type="button"
                  onClick={() => !isAttr && handleAdvanceStatus()}
                  disabled={isAttr}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    isActive && step.key === 'draft'      ? 'bg-gray-200 text-gray-700' :
                    isActive && step.key === 'sent'       ? 'bg-orange-100 text-orange-700' :
                    isDone || (isActive && isAttr)        ? 'bg-green-100 text-green-700' :
                    'bg-white text-gray-400 border border-gray-200'
                  } ${!isAttr ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                >
                  {(isDone || (isActive && isAttr)) ? (
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                  ) : isActive && step.key === 'sent' ? (
                    <EnvelopeIcon className="w-3.5 h-3.5" />
                  ) : (
                    <span className="w-3.5 h-3.5 rounded-full border border-current inline-flex items-center justify-center text-[9px]">{i + 1}</span>
                  )}
                  {step.label}
                  {isAttr && retainedCompany && <span className="font-semibold">: {retainedCompany.name}</span>}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <div className="px-5 py-4 space-y-5">

          {/* ── Entreprises — ligne compacte ── */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-500 flex-shrink-0">Entreprises :</span>

            {consultation.companies.length === 0 && (
              <span className="text-xs text-gray-400 italic">Aucune — ajoutez des entreprises pour comparer les offres.</span>
            )}

            {consultation.companies.map(c => (
              <span key={c.id} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs ${
                c.id === consultation.retainedCompanyId
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-200'
              }`}>
                {c.id === consultation.retainedCompanyId && <CheckCircleIcon className="w-3 h-3 text-green-600" />}
                {c.name}
                {consultation.status !== 'attributed' && (
                  <button type="button" onClick={() => handleRemoveCompany(c.id)}
                    className="ml-0.5 text-gray-300 hover:text-red-400 transition-colors rounded-full p-0.5">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}

            {consultation.status !== 'attributed' && (
              <button type="button" onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-dashed border-gray-300 text-gray-500 hover:border-violet-400 hover:text-violet-600 transition-colors">
                <PlusIcon className="w-3 h-3" />
                {consultation.companies.length === 0 ? 'Ajouter' : 'Modifier'}
              </button>
            )}
          </div>

          {/* ── Comparison table ── */}
          {consultation.companies.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="text-xs w-full border-collapse">

                {/* Group headers */}
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-gray-500 font-medium min-w-[200px] sticky left-0 bg-gray-50 border-r border-gray-200 z-10">
                      Désignation
                    </th>
                    <th colSpan={4} className="px-3 py-2 text-center text-violet-600 font-semibold bg-violet-50 border-r border-violet-200">
                      Estimé interne
                    </th>
                    {consultation.companies.map(c => {
                      const stats = companyStats(c.id);
                      return (
                        <th key={c.id} colSpan={4}
                          className={`px-3 py-2 text-center font-semibold border-r border-gray-200 ${
                            c.id === consultation.retainedCompanyId ? 'bg-green-50 text-green-700' : 'text-gray-700'
                          }`}>
                          {c.name}
                          {c.id === consultation.retainedCompanyId && ' ✓'}
                          {stats.filled < stats.count && (
                            <span className="ml-1.5 text-amber-500 font-normal text-[10px]">
                              {stats.filled}/{stats.count} lignes
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                  <tr className="bg-gray-50 border-b-2 border-gray-200 text-gray-400 font-medium uppercase tracking-wider">
                    <th className="px-3 py-1.5 sticky left-0 bg-gray-50 border-r border-gray-200 z-10" />
                    <th className="px-2 py-1.5 text-right bg-violet-50 w-14">Unité</th>
                    <th className="px-2 py-1.5 text-right bg-violet-50 w-14">Qté</th>
                    <th className="px-2 py-1.5 text-right bg-violet-50 w-24">P.U.</th>
                    <th className="px-2 py-1.5 text-right bg-violet-50 border-r border-violet-200 w-24">Total HT</th>
                    {consultation.companies.map(c => {
                      const bg = c.id === consultation.retainedCompanyId ? 'bg-green-50' : '';
                      return (
                        <React.Fragment key={c.id}>
                          <th className={`px-2 py-1.5 text-right w-14 ${bg}`}>Unité</th>
                          <th className={`px-2 py-1.5 text-right w-14 ${bg}`}>Qté</th>
                          <th className={`px-2 py-1.5 text-right w-24 ${bg}`}>P.U.</th>
                          <th className={`px-2 py-1.5 text-right border-r border-gray-200 w-24 ${bg}`}>Total HT</th>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {consultation.blocks.map(block => {
                    if (!isPrestation(block)) {
                      return (
                        <tr key={block.id} className="bg-gray-50 border-t border-b border-gray-200">
                          <td colSpan={1 + 4 + consultation.companies.length * 4}
                            className="px-3 py-2 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10"
                            style={{ paddingLeft: `${12 + block.level * 16}px` }}>
                            {block.content.titre}
                          </td>
                        </tr>
                      );
                    }

                    const intPU    = parsePU(block.content.prixUnitaire);
                    const intQty   = parseQty(block.content.quantite);
                    const intTotal = intPU * intQty;

                    return (
                      <tr key={block.id} className="hover:bg-gray-50/60">
                        <td className="px-3 py-2 text-gray-800 font-medium sticky left-0 bg-white border-r border-gray-200 z-10 max-w-[200px] truncate"
                          style={{ paddingLeft: `${12 + block.level * 16}px` }}>
                          {block.content.intitule || block.content.titre}
                        </td>

                        {/* Internal */}
                        <td className="px-2 py-2 text-right text-gray-500 bg-violet-50 w-14">{block.content.unite || '—'}</td>
                        <td className="px-2 py-2 text-right text-gray-500 bg-violet-50 w-14">{intQty}</td>
                        <td className="px-2 py-2 text-right text-violet-700 font-medium bg-violet-50 w-24 whitespace-nowrap">
                          {intPU > 0 ? `${fmt(intPU)} €` : '—'}
                        </td>
                        <td className="px-2 py-2 text-right text-violet-700 font-semibold bg-violet-50 border-r border-violet-200 w-24 whitespace-nowrap">
                          {intTotal > 0 ? `${fmt(intTotal)} €` : '—'}
                        </td>

                        {/* Company columns */}
                        {consultation.companies.map(c => {
                          const offer     = getOffer(c.id, block.id);
                          const offerPU   = offer?.prixUnitaire ?? null;
                          const offerQty  = offer?.quantite !== null && offer?.quantite !== undefined ? offer.quantite : intQty;
                          const offerUnite = offer?.unite || block.content.unite || '';
                          const offerTotal = offerPU !== null ? offerPU * offerQty : null;
                          const pct = offerPU !== null && intPU > 0 ? diffPct(intPU, offerPU) : null;
                          const bg  = c.id === consultation.retainedCompanyId ? 'bg-green-50' : '';

                          return (
                            <React.Fragment key={c.id}>
                              <td className={`px-1 py-1 w-14 ${bg}`}>
                                <EditableCell value={offerUnite} placeholder="—"
                                  onChange={v => handleOfferChange(c.id, block.id, 'unite', v)} />
                              </td>
                              <td className={`px-1 py-1 w-14 ${bg}`}>
                                <EditableCell
                                  value={offer?.quantite !== null && offer?.quantite !== undefined ? String(offer.quantite) : ''}
                                  placeholder={String(intQty)} numeric colorClass="text-right text-gray-700"
                                  onChange={v => handleOfferChange(c.id, block.id, 'quantite', v)} />
                              </td>
                              <td className={`px-1 py-1 w-24 ${bg}`}>
                                {offerPU === null ? (
                                  <EditableCell value="" placeholder="—" numeric colorClass="text-right text-gray-300"
                                    onChange={v => handleOfferChange(c.id, block.id, 'prixUnitaire', v)} />
                                ) : (
                                  <div className="flex items-center justify-end gap-0.5">
                                    <EditableCell value={String(offerPU)} numeric
                                      colorClass={`font-medium text-right ${pct !== null && pct < 0 ? 'text-green-600' : pct !== null && pct > 0 ? 'text-red-500' : 'text-gray-700'}`}
                                      onChange={v => handleOfferChange(c.id, block.id, 'prixUnitaire', v)} />
                                    {pct !== null && pct !== 0 && (
                                      <span className={`text-[9px] font-bold flex-shrink-0 ${pct < 0 ? 'text-green-500' : 'text-red-400'}`}>
                                        {pct > 0 ? '+' : ''}{pct}%
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className={`px-2 py-1 text-right font-medium border-r border-gray-200 w-24 whitespace-nowrap ${bg}`}>
                                {offerTotal !== null
                                  ? <span className={pct !== null && pct < 0 ? 'text-green-600' : pct !== null && pct > 0 ? 'text-red-500' : 'text-gray-700'}>{fmt(offerTotal)} €</span>
                                  : <span className="text-gray-200 select-none">—</span>}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* ── Company-specific lines ── */}
                  {(consultation.companyLines ?? []).map(line => {
                    const company = consultation.companies.find(c => c.id === line.companyId);
                    if (!company) return null;
                    const lineTotal = line.prixUnitaire !== null && line.prixUnitaire !== undefined
                      ? line.prixUnitaire * (line.quantite ?? 1)
                      : null;
                    const bg = line.companyId === consultation.retainedCompanyId ? 'bg-green-50' : '';

                    return (
                      <tr key={line.id} className="hover:bg-gray-50/60 border-t border-dashed border-gray-200">
                        {/* Sticky label */}
                        <td className="px-3 py-1.5 sticky left-0 bg-white border-r border-gray-200 z-10">
                          <div className="flex items-center gap-1.5 pl-3">
                            <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                            <EditableCell
                              value={line.intitule} placeholder="Désignation…"
                              colorClass="text-gray-700 italic"
                              onChange={v => handleUpdateCompanyLine(line.id, { intitule: v })}
                            />
                            <span className="text-[9px] text-gray-400 whitespace-nowrap flex-shrink-0">{company.name}</span>
                            <button type="button" onClick={() => handleRemoveCompanyLine(line.id)}
                              className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0 ml-0.5">
                              <XMarkIcon className="w-3 h-3" />
                            </button>
                          </div>
                        </td>

                        {/* Internal columns — empty */}
                        <td colSpan={4} className="bg-violet-50/20 border-r border-violet-100" />

                        {/* Company columns */}
                        {consultation.companies.map(c => {
                          const isOwner = c.id === line.companyId;
                          const colBg = c.id === consultation.retainedCompanyId ? 'bg-green-50' : '';
                          if (!isOwner) {
                            return <React.Fragment key={c.id}><td colSpan={4} className={`border-r border-gray-200 ${colBg}`} /></React.Fragment>;
                          }
                          return (
                            <React.Fragment key={c.id}>
                              <td className={`px-1 py-1 w-14 ${bg}`}>
                                <EditableCell value={line.unite || ''} placeholder="—"
                                  onChange={v => handleUpdateCompanyLine(line.id, { unite: v })} />
                              </td>
                              <td className={`px-1 py-1 w-14 ${bg}`}>
                                <EditableCell
                                  value={line.quantite !== null && line.quantite !== undefined ? String(line.quantite) : ''}
                                  placeholder="1" numeric colorClass="text-right text-gray-700"
                                  onChange={v => { const n = parseFloat(v); handleUpdateCompanyLine(line.id, { quantite: isNaN(n) ? null : n }); }}
                                />
                              </td>
                              <td className={`px-1 py-1 w-24 ${bg}`}>
                                <EditableCell
                                  value={line.prixUnitaire !== null && line.prixUnitaire !== undefined ? String(line.prixUnitaire) : ''}
                                  placeholder="—" numeric colorClass="text-right text-gray-700 font-medium"
                                  onChange={v => { const n = parseFloat(v); handleUpdateCompanyLine(line.id, { prixUnitaire: isNaN(n) ? null : n }); }}
                                />
                              </td>
                              <td className={`px-2 py-1 text-right font-medium border-r border-gray-200 w-24 whitespace-nowrap ${bg}`}>
                                {lineTotal !== null
                                  ? <span className="text-gray-700">{fmt(lineTotal)} €</span>
                                  : <span className="text-gray-200">—</span>}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}

                  {/* ── Add company line row ── */}
                  {consultation.status !== 'attributed' && (
                    <tr className="border-t border-dashed border-gray-200 bg-gray-50/40">
                      <td className="px-3 py-1.5 sticky left-0 bg-gray-50/60 border-r border-gray-200 z-10 text-[10px] text-gray-400 italic">
                        Lignes entreprises
                      </td>
                      <td colSpan={4} className="border-r border-violet-100 bg-violet-50/10" />
                      {consultation.companies.map(c => (
                        <React.Fragment key={c.id}>
                          <td colSpan={4} className="px-2 py-1.5 text-center border-r border-gray-200">
                            <button type="button" onClick={() => handleAddCompanyLine(c.id)}
                              className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-violet-600 transition-colors">
                              <PlusIcon className="w-3 h-3" /> Ajouter
                            </button>
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  )}
                </tbody>

                {/* ── Totals + Retenir ── */}
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-xs">
                    <td className="px-3 py-3 text-gray-700 sticky left-0 bg-gray-50 border-r border-gray-200 z-10">Total</td>
                    <td colSpan={3} className="bg-violet-50" />
                    <td className="px-2 py-3 text-right text-violet-700 font-bold bg-violet-50 border-r border-violet-200 whitespace-nowrap">
                      {fmt(internalTotal)} €
                    </td>
                    {consultation.companies.map(c => {
                      const { total, filled, count } = companyStats(c.id);
                      const pct        = internalTotal > 0 && filled > 0 ? diffPct(internalTotal, total) : null;
                      const isPartial  = filled < count;
                      const isRetained = c.id === consultation.retainedCompanyId;
                      const canRetain  = consultation.status === 'sent' && filled > 0 && !consultation.retainedCompanyId;
                      const bg         = isRetained ? 'bg-green-50' : '';

                      return (
                        <React.Fragment key={c.id}>
                          <td colSpan={3} className={bg} />
                          <td className={`px-2 py-3 text-center border-r border-gray-200 ${bg}`}>
                            {filled > 0 ? (
                              <div>
                                <div className={`font-bold whitespace-nowrap ${pct !== null && pct < 0 ? 'text-green-600' : pct !== null && pct > 0 ? 'text-red-500' : 'text-gray-700'}`}>
                                  {fmt(total)} €
                                  {pct !== null && pct !== 0 && (
                                    <span className={`ml-1 text-[10px] ${pct < 0 ? 'text-green-500' : 'text-red-400'}`}>
                                      {pct > 0 ? '+' : ''}{pct}%
                                    </span>
                                  )}
                                </div>
                                {isPartial && (
                                  <div className="text-[10px] text-amber-500 font-normal">{filled}/{count} lignes</div>
                                )}
                                {canRetain && (
                                  <button type="button" onClick={() => handleRetainCompany(c.id)}
                                    className={`mt-1.5 text-xs px-2.5 py-0.5 rounded-full border font-medium transition-colors ${
                                      isPartial
                                        ? 'border-amber-300 text-amber-600 hover:bg-amber-50'
                                        : 'border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600 hover:bg-green-50'
                                    }`}>
                                    {isPartial ? 'Retenir (partiel)' : 'Retenir'}
                                  </button>
                                )}
                                {isRetained && (
                                  <div className="mt-1 flex items-center justify-center gap-1 text-green-600 text-[10px] font-medium">
                                    <CheckCircleIcon className="w-3 h-3" /> Retenu
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300 font-normal text-[10px]">aucune réponse</span>
                            )}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Attribution banner */}
          {consultation.status === 'attributed' && retainedCompany && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
              <div>
                <strong>{retainedCompany.name}</strong> retenue — les prix ont été reportés dans le chiffrage.
                <span className="block text-xs text-green-600 mt-0.5">
                  Prochainement : import automatique des conditions, quantités et délais.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ConsultationPage;
